import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SectionLink from './SectionLink';

const SECTIONS = [
  { id: 'features', label: 'Features' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'testimonials', label: 'Testimonials' },
];

function Header() {
  // The mobile menu. Below the md breakpoint the links are hidden, so without
  // this state a phone visitor has no way to reach them.
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = () => setMenuOpen(false);

  // Escape closes the open menu and puts focus back on the button that opened
  // it - the disclosure pattern every visitor already knows. Without it the
  // only way out was a second click on the button, and a keyboard visitor was
  // left inside an expanded menu (c500).
  //
  // The handler sits on the nav rather than on `document`, so it fires exactly
  // while focus is on the button or inside the menu, and adds no listener a
  // page has to remember to remove.
  const menuButtonRef = useRef(null);
  const onKeyDown = (event) => {
    if (event.key !== 'Escape' || !menuOpen) return;
    setMenuOpen(false);
    menuButtonRef.current?.focus();
  };

  return (
    <nav className="bg-white shadow-sm sticky top-0 z-50" onKeyDown={onKeyDown}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center p-4">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3" onClick={closeMenu}>
            <img
              src="/logo.png"
              alt="One Stop Greek Logo"
              className="h-10 w-10"
            />
            <span className="text-2xl font-bold" style={{ color: '#a60533' }}>
              One Stop Greek
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-6">
            {SECTIONS.map((section) => (
              <SectionLink
                key={section.id}
                id={section.id}
                className="text-gray-700 transition"
                onMouseEnter={(e) => e.target.style.color = '#a60533'}
                onMouseLeave={(e) => e.target.style.color = ''}
              >
                {section.label}
              </SectionLink>
            ))}
            <Link
              to="/login"
              className="py-2 px-4 rounded-md text-sm border transition"
              style={{ color: '#a60533', borderColor: '#a60533' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = ''}
            >
              Login
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              ref={menuButtonRef}
              type="button"
              aria-label="Main menu"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              onClick={() => setMenuOpen((open) => !open)}
              className="text-gray-700 focus:outline-none transition"
              onMouseEnter={(e) => e.currentTarget.style.color = '#a60533'}
              onMouseLeave={(e) => e.currentTarget.style.color = ''}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d={menuOpen ? 'M6 18L18 6M6 6l12 12' : 'M4 6h16M4 12h16M4 18h16'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation. It overlays the page instead of sitting in the
          flow, so closing it never moves the content under it. The region
          name lets assistive technology announce the panel that opened. */}
      {menuOpen && (
        <div
          id="mobile-menu"
          role="region"
          aria-label="Site menu"
          className="md:hidden absolute inset-x-0 top-full bg-white border-t border-gray-100 shadow-lg"
        >
          <div className="container mx-auto px-4 flex flex-col py-2">
            {SECTIONS.map((section) => (
              <SectionLink
                key={section.id}
                id={section.id}
                className="py-3 px-2 text-gray-700 rounded-md hover:bg-gray-50 transition"
                onClick={closeMenu}
              >
                {section.label}
              </SectionLink>
            ))}
            <Link
              to="/login"
              onClick={closeMenu}
              className="mt-2 mb-1 py-3 px-2 text-center rounded-md text-sm border transition"
              style={{ color: '#a60533', borderColor: '#a60533' }}
            >
              Login
            </Link>
          </div>
        </div>
      )}
    </nav>
  );
}

export default Header;
